import type { ReactNode } from "react";

import { CopyButton } from "~/components/copy-button";
import { cn } from "~/lib/utils";

/**
 * One labelled value in a detail page's definition grid.
 *
 * A <dl> underneath, because that is what these are: the label and the value
 * are a pair, and a screen reader should say so. Wrap a group in <FieldGrid>.
 */
export function DetailField({
  label,
  children,
  copy,
  mono,
  className,
}: {
  label: string;
  children: ReactNode;
  /** The raw value to put on the clipboard. Adds a copy button when set. */
  copy?: string;
  mono?: boolean;
  className?: string;
}) {
  return (
    <div className={cn("min-w-0 space-y-1", className)}>
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="flex min-w-0 items-center gap-1 text-sm">
        <span className={cn("min-w-0 break-all", mono && "font-mono text-[0.8125rem]")}>
          {children}
        </span>
        {copy ? <CopyButton value={copy} label={label} /> : null}
      </dd>
    </div>
  );
}

/** The grid the fields sit in. One column on a phone, three on a desktop. */
export function FieldGrid({
  children,
  className,
}: {
  children: ReactNode;
  className?: string;
}) {
  return (
    <dl className={cn("grid gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3", className)}>
      {children}
    </dl>
  );
}
